import React, { useEffect, useState } from "react";
import Store, { PASSWORD_HINT, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "../../store/Store";

interface Role {
  name: string;
}

interface User {
  username: string;
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  provider?: Provider;
  roles: Role[];
  enabled: boolean;
}

interface Role {
  name: string;
}

interface Permission {
  name: string;
}

interface Provider {
  id?: number;
  company: string;
}

interface StoreType {
  user?: {
    permissions?: Permission[];
  };
  loadUsers: () => Promise<User[] | null>;
  createUser?: (user: User) => Promise<{ user: User | null; error: string | null }>;
  updateUser?: (username: string, user: User) => Promise<{ user: User | null; error: string | null }>;
  deleteUser?: (username: string) => Promise<boolean>;
}

const store = Store as unknown as StoreType;

const UserModal: React.FC<{
  title: string;
  onClose: () => void;
  onSubmit: (user: User) => Promise<string | null>; // resolves with the backend error message (or null on success)
  user?: User;
  roles: Role[];
  providers: Provider[];
}> = ({ title, onClose, onSubmit, user, roles, providers }) => {
  const hasGlobalCreate = store.user?.permissions?.some(
    (p) => p.name === "create_any_user"
  );

  const [form, setForm] = useState<User>({
    username: user?.username || "",
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    company: user?.provider?.company || "",
    roles: user?.roles || [],
    enabled: user?.enabled ?? true,
  });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const availableProviders = providers;
  const availableRoles = roles;

  const isEditMode = !!user;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
      setForm({ ...form, [name]: e.target.checked });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleRoleToggle = (roleName: string) => {
    const exists = form.roles.some((r) => r.name === roleName);
    const newRoles = exists
      ? form.roles.filter((r) => r.name !== roleName)
      : [...form.roles, { name: roleName }];
    setForm({ ...form, roles: newRoles });
  };

  const handleSubmit = async () => {
    const errors: string[] = [];

    if (!form.username.trim()) errors.push("Username is required");
    if (!form.firstName.trim()) errors.push("First name is required");
    if (!form.lastName.trim()) errors.push("Last name is required");
    if (!form.email.trim()) errors.push("Email is required");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.email && !emailRegex.test(form.email)) errors.push("Enter a valid email");

    if (!isEditMode) {
      if (!newPassword.trim()) errors.push("Password is required");
    }

    // NIST SP 800-63B: only the length is checked here (no composition rules), leaked passwords are checked by the backend
    if (newPassword && newPassword.length < PASSWORD_MIN_LENGTH) {
      errors.push(`Das Passwort muss mindestens ${PASSWORD_MIN_LENGTH} Zeichen lang sein.`);
    }
    if (newPassword.length > PASSWORD_MAX_LENGTH) {
      errors.push(`Das Passwort darf höchstens ${PASSWORD_MAX_LENGTH} Zeichen lang sein.`);
    }

    if (newPassword !== confirmPassword) {
      errors.push("Passwords do not match");
    }

    if (errors.length > 0) {
      setError(errors.join("\n"));
      return;
    }

    const payload = {
      ...form,
      password: newPassword ? newPassword : undefined,
    };

    setError(null);
    setSaving(true);
    try {
      const backendError = await onSubmit(payload as User);
      if (backendError) setError(backendError); // e.g. PASSWORD_COMPROMISED -> keep the modal open
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-2xl">
        <h2 className="text-xl font-semibold mb-4">{title}</h2>

        {error && (
          <div className="bg-red-100 text-red-700 p-2 mb-2 rounded">
            {error.split("\n").map((e, i) => (
              <div key={i}>• {e}</div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <input className="border p-2 rounded"
            data-pw="input-username"
            name="username"
            placeholder="Username"
            value={form.username}
            onChange={handleChange}
          />
          <input className="border p-2 rounded"
            data-pw="input-email"
            name="email"
            placeholder="Email"
            type="email"
            value={form.email}
            onChange={handleChange}
          />
          <input className="border p-2 rounded"
            data-pw="input-first-name"
            name="firstName"
            placeholder="First Name"
            value={form.firstName}
            onChange={handleChange}
          />
          <input className="border p-2 rounded"
            data-pw="input-last-name"
            name="lastName"
            placeholder="Last Name"
            value={form.lastName}
            onChange={handleChange}
          />

          <input className="border p-2 rounded"
            data-pw="input-new-password"
            name="newPassword"
            placeholder="New Password"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
          <input className="border p-2 rounded"
            data-pw="input-new-password-confirm"
            name="newPasswordConfirm"
            placeholder="Confirm Password"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />

          <p className="text-xs text-gray-500 col-span-2 -mt-2 mb-1" data-pw="text-password-hint">
            {PASSWORD_HINT}
          </p>

          {isEditMode && (
            <p className="text-xs text-gray-500 col-span-2 -mt-2 mb-1">
              Leave password fields empty to keep current password
            </p>
          )}

          <div className="col-span-2 flex items-center space-x-2 mt-2">
            <input
              data-pw="checkbox-enabled"
              type="checkbox"
              name="enabled"
              checked={form.enabled}
              onChange={handleChange}
              id="enabled"
            />
            <label htmlFor="enabled" className="text-gray-700">
              Enabled
            </label>
          </div>

          {hasGlobalCreate && (
            <div className="col-span-2">
              <label className="block text-gray-700 text-sm mb-1">Provider</label>
              <select className="border p-2 rounded w-full"
                data-pw="select-provider"
                name="company"
                value={form.company}
                onChange={handleChange}
              >
                <option value="">Select provider...</option>
                {availableProviders.map((prov) => (
                  <option key={prov.id} value={prov.company}>
                    {prov.company}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="col-span-2 mt-2">
            <label className="block text-gray-700 text-sm mb-1">Roles</label>
            <div className="flex flex-wrap gap-3">
              {availableRoles.map((role) => (
                <label key={role.name} className="flex items-center space-x-2">
                  <input
                    data-pw={"checkbox-role-" + role.name}
                    type="checkbox"
                    checked={form.roles.some((r) => r.name === role.name)}
                    onChange={() => handleRoleToggle(role.name)}
                  />
                  <span>{role.name.replace(/^ROLE_/, "")}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6 space-x-3">
          <button data-pw="button-cancel" onClick={onClose} className="px-4 py-2 rounded border">
            Cancel
          </button>
          <button className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700 disabled:opacity-50"
            data-pw="button-save"
            onClick={handleSubmit}
            disabled={saving}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmModal: React.FC<{
  title: string;
  message: string;
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmPw?: string;
}> = ({ title, message, onCancel, onConfirm, confirmLabel = "Delete", cancelLabel = "Cancel", confirmPw = "button-confirm-delete" }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
    <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm">
      <h2 className="text-lg font-semibold mb-3">{title}</h2>
      <p className="text-gray-700 mb-6">{message}</p>
      <div className="flex justify-end space-x-3">
        <button data-pw="button-confirm-cancel" onClick={onCancel} className="px-4 py-2 rounded border">{cancelLabel}</button>
        <button
          data-pw={confirmPw}
          onClick={onConfirm}
          className="bg-red-600 text-white px-4 py-2 rounded shadow hover:bg-red-700"
        >
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);

  const hasAnyPermission = (allowed: string[]): boolean =>
    Array.isArray(store.user?.permissions) &&
    store.user!.permissions!.some((p) => allowed.includes(p.name));

  const canCreate = hasAnyPermission(["create_any_user", "create_provider_user"]);
  const canEdit = hasAnyPermission(["edit_any_user", "edit_provider_user"]);
  const canDelete = hasAnyPermission(["delete_any_user", "delete_provider_user"]);
  const isAdministrator = hasAnyPermission(["administration"]); // 2FA admin actions

  const [showTwoFactorResetModal, setShowTwoFactorResetModal] = useState(false);
  const [showRolloutModal, setShowRolloutModal] = useState(false);
  const [adminBusy, setAdminBusy] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    const canCreateAny = hasAnyPermission(["create_any_user"]);
    const canEditAny = hasAnyPermission(["edit_any_user"]);

    const usersDataPromise = store.loadUsers();
    const rolesPromise = Store.fetch("admin/roles");

    const providersPromise = (canCreateAny || canEditAny)
      ? Store.fetch("admin/providers")
      : Promise.resolve([]);

    const [usersData, rolesData, providersData] = await Promise.all([
      usersDataPromise, rolesPromise, providersPromise
    ]);

    if (!usersData) setError("Failed to load users");
    else setUsers(usersData);

    if (Array.isArray(rolesData)) setRoles(rolesData);
    if (Array.isArray(providersData)) setProviders(providersData);

    setLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  // returns the backend error message (shown inside the modal) or null on success
  const handleCreate = async (user: User): Promise<string | null> => {
    const result = await store.createUser?.(user);
    if (result?.error) return result.error;
    setShowCreateModal(false);
    await loadUsers();
    return null;
  };

  const handleUpdate = async (user: User): Promise<string | null> => {
    if (!selectedUser) return null;
    const result = await store.updateUser?.(selectedUser.username, user);
    if (result?.error) return result.error;
    setShowEditModal(false);
    await loadUsers();
    return null;
  };

  const handleSendTwoFactorLink = async (u: User) => {
    setAdminBusy(true);
    try {
      const result = await Store.sendTwoFactorEnrollmentLink(u.username);
      if (result.ok) {
        Store.setPopupMessage("ok", "2FA-Link gesendet", `Ein neuer Einrichtungslink wurde an ${u.email || u.username} gesendet. Ältere Links sind ungültig.`);
      }
    } finally {
      setAdminBusy(false);
    }
  };

  const handleResetTwoFactor = async () => {
    if (!selectedUser) return;
    setShowTwoFactorResetModal(false);
    setAdminBusy(true);
    try {
      const result = await Store.resetUserTwoFactor(selectedUser.username);
      if (result.ok) {
        Store.setPopupMessage("ok", "2FA zurückgesetzt", `Die 2FA von ${selectedUser.username} wurde zurückgesetzt. Ein neuer Einrichtungslink wurde per E-Mail gesendet.`);
      }
    } finally {
      setAdminBusy(false);
    }
  };

  const handleRollout = async () => {
    setShowRolloutModal(false);
    setAdminBusy(true);
    try {
      const result = await Store.rolloutTwoFactorEnrollmentLinks();
      if (result.ok) {
        const sent = Number(result.data?.sent ?? 0);
        Store.setPopupMessage("ok", "2FA-Links versendet", `${sent} ${sent === 1 ? "Link wurde" : "Links wurden"} versendet.`, 8000);
      }
    } finally {
      setAdminBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    await store.deleteUser?.(selectedUser.username);
    setShowDeleteModal(false);
    await loadUsers();
  };

  return (
    <div className="mx-4 rounded-2xl p-6 overflow-auto">
      <div className="flex gap-x-6 items-center mb-4">
        <h1 className="text-2xl font-semibold text-gray-800">User Management</h1>
        {canCreate && (
          <button
            data-pw="button-create-user"
            className="bg-blue-600 text-white px-4 py-2 rounded-xl shadow hover:bg-blue-700 transition"
            onClick={() => setShowCreateModal(true)}
          >
            + Create User
          </button>
        )}
        {isAdministrator && (
          <button
            data-pw="button-2fa-rollout"
            className="bg-white text-blue-700 border border-blue-600 px-4 py-2 rounded-xl shadow hover:bg-blue-50 transition disabled:opacity-50"
            disabled={adminBusy}
            onClick={() => setShowRolloutModal(true)}
            title="Einmalig allen Benutzern mit 2FA-Pflicht ohne eingerichtete 2FA einen Einrichtungslink senden"
          >
            2FA-Links an alle senden
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-gray-500">Loading users...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : (
        <table className="min-w-full border-collapse bg-white shadow rounded-xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 text-left text-gray-600 text-sm uppercase tracking-wider">
              <th className="py-3 px-4">Username</th>
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Email</th>
              <th className="py-3 px-4">Company</th>
              <th className="py-3 px-4">Roles</th>
              <th className="py-3 px-4">Enabled</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.username} className="border-b hover:bg-gray-50 transition">
                <td className="py-3 px-4 font-medium">{u.username}</td>
                <td className="py-3 px-4">{u.firstName} {u.lastName}</td>
                <td className="py-3 px-4">{u.email}</td>
                <td className="py-3 px-4">{u.provider?.company}</td>
                <td className="py-3 px-4">{u.roles.map(r => r.name.replace(/^ROLE_/, "")).join(", ")}</td>
                <td className="py-3 px-4">
                  <span className={`py-1 px-2 text-xs font-semibold rounded-full ${u.enabled ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {u.enabled ? "Yes" : "No"}
                  </span>
                </td>
                <td className="py-3 px-4 text-right">
                  {canEdit && (
                    <button data-pw="button-edit-user" className="text-blue-600 hover:text-blue-800 font-medium mr-3"
                      onClick={() => { setSelectedUser(u); setShowEditModal(true); }}
                    >
                      Edit
                    </button>
                  )}
                  {canDelete && (
                    <button data-pw="button-delete-user" className="text-red-500 hover:text-red-700 font-medium"
                      onClick={() => { setSelectedUser(u); setShowDeleteModal(true); }}
                    >
                      Delete
                    </button>
                  )}
                  {isAdministrator && (
                    <>
                      <button data-pw="button-2fa-link-user" className="text-blue-600 hover:text-blue-800 font-medium ml-3 disabled:opacity-50"
                        disabled={adminBusy}
                        onClick={() => handleSendTwoFactorLink(u)}
                      >
                        2FA-Link senden
                      </button>
                      <button data-pw="button-2fa-reset-user" className="text-red-500 hover:text-red-700 font-medium ml-3 disabled:opacity-50"
                        disabled={adminBusy}
                        onClick={() => { setSelectedUser(u); setShowTwoFactorResetModal(true); }}
                      >
                        2FA zurücksetzen
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreateModal && (
        <UserModal
          title="Create User"
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreate}
          roles={roles}
          providers={providers}
        />
      )}

      {showEditModal && selectedUser && (
        <UserModal
          title="Edit User"
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdate}
          user={selectedUser}
          roles={roles}
          providers={providers}
        />
      )}

      {showDeleteModal && selectedUser && (
        <ConfirmModal
          title="Delete User"
          message={`Are you sure you want to delete ${selectedUser.username}?`}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
      )}

      {showTwoFactorResetModal && selectedUser && (
        <ConfirmModal
          title="2FA zurücksetzen"
          message={`Soll die 2-Faktor-Authentifizierung von ${selectedUser.username} wirklich zurückgesetzt werden? Die bisherige Authenticator-App funktioniert danach nicht mehr, der Benutzer erhält automatisch einen neuen Einrichtungslink per E-Mail.`}
          confirmLabel="2FA zurücksetzen"
          cancelLabel="Abbrechen"
          confirmPw="button-confirm-2fa-reset"
          onCancel={() => setShowTwoFactorResetModal(false)}
          onConfirm={handleResetTwoFactor}
        />
      )}

      {showRolloutModal && (
        <ConfirmModal
          title="2FA-Links an alle senden"
          message="Allen Benutzern mit 2FA-Pflicht, die die 2FA noch nicht eingerichtet haben, wird ein Einrichtungslink per E-Mail gesendet. Fortfahren?"
          confirmLabel="Links senden"
          cancelLabel="Abbrechen"
          confirmPw="button-confirm-2fa-rollout"
          onCancel={() => setShowRolloutModal(false)}
          onConfirm={handleRollout}
        />
      )}
    </div>
  );
};

export default UsersPage;
